/**
 * Détection des pannes SILENCIEUSES (CCT-226).
 *
 * POURQUOI CE MODULE EXISTE
 * -------------------------
 * Toute notre télémétrie décrit des ACTIONS et des ÉCHECS : un appel réseau, une navigation,
 * une exception, une `console.error`. Un écran qui cesse d'avancer ne fait ni l'un ni
 * l'autre — il n'échoue pas, il n'agit plus. Son silence est rigoureusement indistinguable
 * de celui d'un apprenant qui a arrêté de lire.
 *
 * Mesure qui a motivé ce module : une scène bloquée a produit une session de 218 événements,
 * ZÉRO erreur HTTP, ZÉRO erreur console. Il n'y avait rien à voir — pas quelque chose qu'on
 * avait manqué. Une panne qui n'émet rien est indétectable par construction, et aucune
 * relecture de logs, humaine ou automatique, ne la trouvera.
 *
 * On inverse donc la charge : un écran DÉCLARE ce qu'il attend. Si l'attente dure au-delà du
 * seuil, c'est une anomalie, et elle est émise.
 *
 * ⚠️ LE PIÈGE À ÉVITER EST LE FAUX POSITIF. « L'écran n'avance plus » et « l'utilisateur est
 * parti » se ressemblent beaucoup côté client. Trois garanties les séparent :
 *   * on n'observe QUE lorsque la page est visible — un onglet en arrière-plan voit ses
 *     minuteurs bridés par le navigateur, et l'utilisateur ne regarde de toute façon pas ;
 *   * l'écran passe `active: false` dès qu'il n'attend plus rien, donc une attente terminée
 *     ne peut pas être signalée après coup ;
 *   * une même attente n'est signalée QU'UNE FOIS. Sans cela, un écran laissé ouvert
 *     inonderait la session et la rendrait illisible — on aurait remplacé le silence par du
 *     bruit, ce qui n'est pas un progrès.
 */

import { useEffect, useRef } from 'react';

import { track } from './beacon';

/** Seuil par défaut. Assez long pour ne pas confondre lenteur et panne. */
export const DEFAULT_WAIT_MS = 12000;

export interface Wait {
  /** Écran concerné, tel qu'il apparaîtra dans la timeline (ex. « SceneReader »). */
  screen: string;
  /**
   * CE QUI EST ATTENDU, sous forme de clé stable et parlante (ex. « beat:12 », « reponse-ia »).
   *
   * Elle sert à deux choses : identifier l'attente dans le log, et détecter qu'elle a changé
   * — donc que l'écran a avancé. Une clé qui change à chaque rendu ne détecterait jamais
   * rien ; une clé constante confondrait deux attentes successives.
   */
  expected: string;
  /** Vrai tant que l'écran attend. Le passer à `false` désarme immédiatement. */
  active: boolean;
  /** Seuil propre à cette attente, si la valeur par défaut ne convient pas. */
  thresholdMs?: number;
  /** Contexte utile au diagnostic, joint à l'anomalie. */
  context?: Record<string, unknown>;
}

/** Vrai si l'utilisateur a la page sous les yeux (toujours vrai hors navigateur). */
function pageVisible(): boolean {
  try {
    if (typeof document === 'undefined' || !('visibilityState' in document)) return true;
    return document.visibilityState === 'visible';
  } catch {
    return true;
  }
}

/**
 * Signale une attente qui ne se résout pas.
 *
 * L'écran appelle ce hook en décrivant ce qu'il attend ; il n'a rien d'autre à faire. Aucune
 * exception ne remonte : l'observation ne doit jamais devenir une cause de panne.
 */
export function useLiveness({ screen, expected, active, thresholdMs, context }: Wait): void {
  // Attentes déjà signalées, pour ne pas répéter. Une par montage : deux écrans distincts
  // qui attendent la même chose sont deux anomalies, pas une.
  const reported = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!active || !expected) return;
    const threshold = thresholdMs ?? DEFAULT_WAIT_MS;
    const start = Date.now();
    const key = `${screen}|${expected}`;

    const t = setTimeout(() => {
      // ⚠️ RE-VÉRIFIÉ À L'ÉCHÉANCE, pas seulement à l'armement : l'onglet a pu passer en
      // arrière-plan entre-temps, et le minuteur d'un onglet caché se déclenche en retard.
      // Signaler là accuserait le produit d'une lenteur que le navigateur a imposée.
      if (!pageVisible() || reported.current.has(key)) return;
      reported.current.add(key);
      try {
        track({
          type: 'anomalie',
          action: 'attente_bloquee',
          path: `/${screen}`,
          meta: { expected, seuil_ms: threshold, ecoule_ms: Date.now() - start, ...(context ?? {}) },
        });
      } catch {
        /* l'observation ne doit jamais casser l'écran observé */
      }
    }, threshold);

    return () => clearTimeout(t);
  // `context` est volontairement hors dépendances : il change d'identité à chaque rendu et
  // relancerait le minuteur en boucle, ce qui empêcherait toute anomalie d'être signalée.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, expected, active, thresholdMs]);
}
